import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Download, Search } from 'lucide-react';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

export default function StatementDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [stmt, setStmt] = useState(null);
  const [lines, setLines] = useState([]);
  const [yt, setYt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterArtist, setFilterArtist] = useState('');
  const [filterTrack, setFilterTrack] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/statements/${id}`); setStmt(data.statement); setLines(data.lines || []); setYt(data.youtube_adsense); }
      catch (e) { toast.error('Errore'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  // Unique values for filters
  const artists = useMemo(() => [...new Set(lines.map(l => l.artist_name).filter(Boolean))].sort(), [lines]);
  const tracks = useMemo(() => {
    const source = filterArtist ? lines.filter(l => l.artist_name === filterArtist) : lines;
    return [...new Set(source.map(l => l.track_title).filter(Boolean))].sort();
  }, [lines, filterArtist]);
  const platforms = useMemo(() => {
    let source = lines;
    if (filterArtist) source = source.filter(l => l.artist_name === filterArtist);
    if (filterTrack) source = source.filter(l => l.track_title === filterTrack);
    return [...new Set(source.map(l => l.platform).filter(Boolean))].sort();
  }, [lines, filterArtist, filterTrack]);

  // Filtered lines
  const filtered = useMemo(() => {
    let f = lines;
    if (filterArtist) f = f.filter(l => l.artist_name === filterArtist);
    if (filterTrack) f = f.filter(l => l.track_title === filterTrack);
    if (filterPlatform) f = f.filter(l => l.platform === filterPlatform);
    return f;
  }, [lines, filterArtist, filterTrack, filterPlatform]);

  // Filtered totals
  const filteredTotal = useMemo(() => ({
    streams: filtered.reduce((s, l) => s + (l.streams || 0), 0),
    userShare: filtered.reduce((s, l) => s + parseFloat(l.user_share || 0), 0),
    pnShare: filtered.reduce((s, l) => s + parseFloat(l.proudnoize_share || 0), 0),
    net: filtered.reduce((s, l) => s + parseFloat(l.net_amount || 0), 0),
  }), [filtered]);

  const hasFilters = filterArtist || filterTrack || filterPlatform;
  const downloadPdf = async () => {
    try {
      const response = await api.get(`/statements/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `rendiconto_${stmt.period_month}_${stmt.period_year}.pdf`; a.click();
    } catch (e) { toast.error('PDF non disponibile'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div>;
  if (!stmt) return <p>Non trovato</p>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/statements" className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={20} /></Link>
        <div className="flex-1"><h1 className="text-xl font-bold text-pn-dark">{MONTHS[stmt.period_month - 1]} {stmt.period_year}</h1><p className="text-sm text-gray-400">{stmt.label_name || ''}</p></div>
        {stmt.pdf_path && <button onClick={downloadPdf} className="btn-primary inline-flex items-center gap-2"><Download size={16} />PDF</button>}
      </div>

      {/* Totals - NO lordo, NO fee distributore */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="card p-4"><p className="text-xs text-gray-500">Importo</p><p className="text-lg font-bold">€{parseFloat(stmt.net_total).toFixed(2)}</p></div>
        <div className="card p-4"><p className="text-xs text-gray-500">Commissione PN ({stmt.pn_commission_percent || ''}%)</p><p className="text-lg font-bold text-orange-500">-€{parseFloat(stmt.proudnoize_share).toFixed(2)}</p></div>
        <div className="card p-4 bg-gradient-to-br from-pn-dark to-pn-blue text-white"><p className="text-xs opacity-80">Il Tuo Totale</p><p className="text-lg font-bold">€{(parseFloat(stmt.user_share) + (yt ? parseFloat(yt.user_share) : 0)).toFixed(2)}</p></div>
      </div>

      {yt && <div className="card p-4 mb-6 bg-red-50 border-red-100"><div className="flex items-center justify-between"><div><span className="text-sm font-medium text-red-800">YouTube AdSense</span>{yt.channel_name && <span className="text-xs text-red-600 ml-2">{yt.channel_name}</span>}</div><span className="font-bold text-red-800">€{parseFloat(yt.user_share).toFixed(2)}</span></div></div>}

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        {artists.length > 1 && (
          <select className="input-field w-48 text-sm" value={filterArtist} onChange={e => { setFilterArtist(e.target.value); setFilterTrack(''); setFilterPlatform(''); }}>
            <option value="">Tutti gli artisti</option>
            {artists.map((a, i) => <option key={i} value={a}>{a}</option>)}
          </select>
        )}
        {tracks.length > 1 && (
          <select className="input-field w-48 text-sm" value={filterTrack} onChange={e => { setFilterTrack(e.target.value); setFilterPlatform(''); }}>
            <option value="">Tutti i brani</option>
            {tracks.map((t, i) => <option key={i} value={t}>{t}</option>)}
          </select>
        )}
        {platforms.length > 1 && (
          <select className="input-field w-48 text-sm" value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}>
            <option value="">Tutte le piattaforme</option>
            {platforms.map((p, i) => <option key={i} value={p}>{p}</option>)}
          </select>
        )}
        {hasFilters && <button onClick={() => { setFilterArtist(''); setFilterTrack(''); setFilterPlatform(''); }} className="text-xs text-pn-red hover:underline">Reset</button>}
      </div>

      {/* Filtered summary */}
      {hasFilters && (
        <div className="card p-4 mb-4 bg-yellow-50 border-yellow-200">
          <p className="text-xs text-yellow-700 mb-1">Filtro attivo — {filtered.length}/{lines.length} righe</p>
          <div className="flex gap-6 text-sm">
            <span><strong>{filteredTotal.streams.toLocaleString()}</strong> stream</span>
            <span>Importo: <strong>€{filteredTotal.net.toFixed(2)}</strong></span>
            <span>Il tuo: <strong className="text-green-600">€{filteredTotal.userShare.toFixed(2)}</strong></span>
          </div>
        </div>
      )}

      {/* Lines table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ISRC</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Artista</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Titolo</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Piattaforma</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Stream</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Importo</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Il Tuo</th>
            </tr></thead>
            <tbody className="divide-y">
              {filtered.map((l, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-400 font-mono">{l.isrc || '-'}</td>
                  <td className="px-3 py-2">{l.artist_name || '-'}</td>
                  <td className="px-3 py-2">{l.track_title || '-'}</td>
                  <td className="px-3 py-2 text-gray-500">{l.platform || '-'}</td>
                  <td className="px-3 py-2 text-right">{(l.streams || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">€{parseFloat(l.net_amount || 0) < 0.01 ? parseFloat(l.net_amount || 0).toFixed(6) : parseFloat(l.net_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-medium text-green-600">€{parseFloat(l.user_share || 0) < 0.01 ? parseFloat(l.user_share || 0).toFixed(6) : parseFloat(l.user_share || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
